import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";
import { ACCESSORY_CATEGORIES, BRANDS, HARDWARE_HIGHLIGHTS } from "../data/content";
import { IMGS } from "../lib/img";

export default function Accessories() {
  return (
    <>
      <SEO
        title="Kitchen Accessories & Hardware — Hettich, Häfele, Blum | Paramount Kitchen"
        description="Premium kitchen hardware and accessories from established brands — drawer systems, soft-close hinges, pull-out baskets, magic corners, tandem drawers, tall units and organised storage, selected per project."
      />

      <section className="bg-royal pb-20 pt-36 text-ivory md:pb-28 md:pt-44">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="eyebrow text-gold">Accessories &amp; Hardware</p>
            <h1 className="text-balance mt-5 max-w-3xl font-display text-4xl font-medium leading-tight md:text-6xl">
              Hardware that works as{" "}
              <span className="serif-accent text-gold">beautifully as it looks.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-ivory/65">
              Paramount Kitchen can offer kitchen hardware and accessories from established brands,
              subject to project requirements and availability.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div data-testid="brands-strip" className="mt-14 flex flex-wrap items-center gap-x-12 gap-y-6 border-t border-white/10 pt-10">
              {BRANDS.map((b) => (
                <span key={b} className="font-display text-2xl font-medium tracking-wide text-ivory/50 transition-colors duration-300 hover:text-gold">
                  {b}
                </span>
              ))}
              <span className="text-xs uppercase tracking-[0.18em] text-ivory/40">
                + other suitable premium hardware options
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow text-golddim">Premium Motion</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Every drawer, every hinge, <span className="serif-accent text-golddim">on purpose.</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-ink/70">
              Hardware is where a kitchen is used a dozen times a day. We offer soft-close hinges,
              3D adjustable hinges, premium drawer systems and pull-out mechanisms — installed and
              calibrated to move smoothly for years.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {HARDWARE_HIGHLIGHTS.map((h) => (
                <span
                  key={h}
                  className="rounded-full border border-ink/15 bg-white px-4 py-2 text-xs font-semibold text-ink/70"
                >
                  {h}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-5">
              <img
                src={IMGS.hinge}
                alt="Premium soft-close hinge installed inside a Paramount Kitchen cabinet"
                loading="lazy"
                className="aspect-square w-full rounded-2xl object-cover shadow-md"
              />
              <img
                src={IMGS.baskets}
                alt="Organised kitchen drawer storage accessories"
                loading="lazy"
                className="mt-8 aspect-square w-full rounded-2xl object-cover shadow-md"
              />
              <img
                src={IMGS.drawers}
                alt="Premium kitchen drawer system with cutlery organiser"
                loading="lazy"
                className="aspect-square w-full rounded-2xl object-cover shadow-md"
              />
              <div className="mt-8 flex items-center justify-center rounded-2xl bg-royal p-6 text-center text-ivory">
                <p className="font-display text-xl leading-snug">
                  Selected for <span className="serif-accent text-gold">quiet strength.</span>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-24 md:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="max-w-2xl">
            <p className="eyebrow text-golddim">Storage Systems</p>
            <h2 className="text-balance mt-5 font-display text-4xl font-medium leading-tight text-ink md:text-5xl">
              Every inch has <span className="serif-accent text-golddim">a purpose.</span>
            </h2>
            <p className="mt-6 leading-relaxed text-ink/65">
              Accessories are selected according to your kitchen layout, storage requirements and
              personal preference — so the kitchen works the way you cook.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
            {ACCESSORY_CATEGORIES.map((a, i) => (
              <Reveal key={a.name} delay={(i % 7) * 0.05} className="h-full">
                <div
                  data-testid={`accessory-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group flex h-full flex-col items-center gap-4 rounded-2xl border border-ink/10 bg-white p-6 text-center transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-lg"
                >
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-sand text-wood transition-colors duration-300 group-hover:bg-gold group-hover:text-royal">
                    <a.icon className="h-5 w-5" />
                  </span>
                  <span className="text-xs font-bold uppercase leading-snug tracking-[0.1em] text-ink/75">
                    {a.name}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
